import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { FORCAS_DIAGNOSTICO } from '@/features/cinco-forcas/index.js';
import { FLAT_QUESTIONS } from '@/features/cinco-forcas/flat-questions.js';
import { RadarChart, compareSeriesColor } from '@/features/cinco-forcas/radar.js';

type CompareData = {
  respostas: {
    user: { id: string; name: string };
    respostas: number[];
    scores: { forcas: number[] };
  }[];
  divergencias: { pergunta_index: number; min: number; max: number; delta: number }[];
};

function buildForceInsights(data: CompareData): string[] {
  const insights: string[] = [];
  const { respostas } = data;
  if (respostas.length < 2) return insights;

  FORCAS_DIAGNOSTICO.forEach((f, fi) => {
    const vals = respostas.map((r) => ({
      name: r.user.name.split(' ')[0] ?? r.user.name,
      score: r.scores.forcas[fi] ?? 0,
    }));
    let maxD = 0;
    let a = vals[0]!;
    let b = vals[1]!;
    for (let i = 0; i < vals.length; i++) {
      for (let j = i + 1; j < vals.length; j++) {
        const d = Math.abs(vals[i]!.score - vals[j]!.score);
        if (d > maxD) {
          maxD = d;
          a = vals[i]!;
          b = vals[j]!;
        }
      }
    }
    if (maxD >= 2) {
      const hi = a.score >= b.score ? a : b;
      const lo = a.score >= b.score ? b : a;
      insights.push(
        `${f.nome}: ${hi.name} avalia ${hi.score.toFixed(1).replace('.', ',')}, ${lo.name} avalia ${lo.score.toFixed(1).replace('.', ',')} — diferença de ${maxD.toFixed(1).replace('.', ',')} pontos. Vale uma conversa sobre ${f.temas[0]!.nome}.`,
      );
    }
  });
  return insights;
}

export function ComparePage() {
  const { id } = useParams();
  const q = useQuery({
    queryKey: ['cmp', id],
    queryFn: async () => (await api.get<CompareData>(`/diagnosticos/${id}/comparar`)).data,
    enabled: !!id,
  });

  if (!id || q.isLoading) return <p className="p-8 text-ha-muted">Carregando…</p>;
  if (!q.data || q.data.respostas.length < 2) {
    return (
      <p className="p-8 text-ha-muted">
        São necessárias pelo menos 2 respostas. <Link to={`/diagnostico/${id}`} className="text-accent underline">Voltar</Link>
      </p>
    );
  }

  const { respostas, divergencias } = q.data;
  const radarSeries = respostas.map((r, i) => {
    const { stroke, fill } = compareSeriesColor(i);
    return {
      scores: r.scores.forcas,
      stroke,
      fill,
      label: r.user.name,
    };
  });
  const insights = buildForceInsights(q.data);

  return (
    <div className="mx-auto max-w-4xl pb-16">
      <Link to={`/diagnostico/${id}`} className="mb-4 inline-block text-xs text-ha-muted underline">
        ← Resultado
      </Link>
      <h1 className="mb-4 text-2xl font-black">Comparar perspectivas</h1>
      <p className="mb-6 text-sm text-ha-muted">{respostas.length} respondentes</p>

      <RadarChart series={radarSeries} />

      <div className="mb-8 overflow-x-auto">
        <table className="w-full min-w-[480px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-ha-border text-left text-[10px] uppercase text-ha-muted">
              <th className="p-2">Força</th>
              {respostas.map((r) => (
                <th key={r.user.id} className="p-2">
                  {r.user.name}
                </th>
              ))}
              <th className="p-2">Δ</th>
            </tr>
          </thead>
          <tbody>
            {FORCAS_DIAGNOSTICO.map((f, fi) => {
              const cols = respostas.map((r) => r.scores.forcas[fi] ?? 0);
              const delta = Math.max(...cols) - Math.min(...cols);
              return (
                <tr key={f.num} className="border-b border-ha-border">
                  <td className="p-2 font-bold" style={{ color: f.texto }}>
                    {f.nome}
                  </td>
                  {cols.map((s, i) => (
                    <td key={respostas[i]!.user.id} className="p-2">
                      {s.toFixed(1).replace('.', ',')}
                    </td>
                  ))}
                  <td className="p-2 font-bold text-accent">{delta.toFixed(1).replace('.', ',')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {insights.length > 0 && (
        <section className="mb-8 border border-ha-border bg-ha-card2 p-4">
          <h2 className="mb-3 text-xs font-bold uppercase text-ha-muted">Insights</h2>
          <ul className="space-y-2 text-sm text-ha-subtle">
            {insights.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2 className="mb-3 text-xs font-bold uppercase text-ha-muted">Maiores divergências (Δ≥2)</h2>
        {divergencias.length === 0 ? (
          <p className="text-sm text-ha-muted">Nenhuma pergunta com diferença ≥ 2 pontos.</p>
        ) : (
          <ul className="space-y-4">
            {divergencias.map((d) => (
              <li key={d.pergunta_index} className="border border-ha-border bg-ha-card2 p-3 text-sm">
                <p className="mb-2 font-semibold text-ha-text">
                  P{d.pergunta_index + 1}: {FLAT_QUESTIONS[d.pergunta_index]}
                </p>
                <p className="text-xs text-ha-muted">
                  {respostas.map((r) => (
                    <span key={r.user.id} className="mr-3">
                      {r.user.name}: {r.respostas[d.pergunta_index]}
                    </span>
                  ))}
                  · Δ {d.delta.toFixed(1)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
